import React, { useEffect } from "react";
import { Notice } from "../services/NoticeService";

const TONE_FG = {
  info: "#5EE9B5",
  warning: "#F5B44C",
  critical: "#F0616B",
};

/**
 * Full-screen notice, centred over a dimmed backdrop.
 *
 * For artwork that has to be read as a whole — posters, roadmaps, launch
 * announcements — where a top banner strip would crop it into uselessness.
 */
const NoticePopup: React.FC<{ notice: Notice; onDismiss: () => void }> = ({
  notice,
  onDismiss,
}) => {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onDismiss();
    window.addEventListener("keydown", esc);
    // Stop the page behind from scrolling while the popup owns the screen.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = prev;
    };
  }, [onDismiss]);

  const fg = TONE_FG[notice.tone] ?? TONE_FG.warning;

  return (
    <div
      onClick={onDismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(0,2,5,0.82)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'Sora',sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="cvt-scroll"
        style={{
          position: "relative",
          width: "min(460px,100%)",
          maxHeight: "92vh",
          overflowY: "auto",
          borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "#060B08",
          boxShadow: "0 24px 70px rgba(0,0,0,0.6)",
        }}
      >
        {/* Floats over the artwork so a full-bleed image is not pushed down
            by a header bar. */}
        <button
          onClick={onDismiss}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 2,
            width: 30,
            height: 30,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(4px)",
            color: "#F2F7F4",
            cursor: "pointer",
            fontSize: 17,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </button>

        {notice.imageUrl && (
          <img
            src={notice.imageUrl}
            alt={notice.title || "Announcement"}
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        )}

        {(notice.title || notice.body) && (
          <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 6 }}>
            {notice.title && (
              <div style={{ fontSize: 16, fontWeight: 600, color: fg }}>{notice.title}</div>
            )}
            {notice.body && (
              <div style={{ fontSize: 13, color: "#D6E2DC", lineHeight: 1.55 }}>
                {notice.body}
              </div>
            )}
          </div>
        )}

        <div style={{ padding: notice.title || notice.body ? "0 18px 18px" : 18 }}>
          <button
            onClick={onDismiss}
            style={{
              width: "100%",
              border: 0,
              borderRadius: 9,
              padding: "11px 16px",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "'Sora',sans-serif",
              cursor: "pointer",
              background: "linear-gradient(180deg, #19B37C, #0E8A5C)",
              color: "#03150D",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoticePopup;
